<!-- Slot for the client-specific page header (e.g. web/desktop <app-header>). -->
<ng-content></ng-content>

<bit-table-v2
  #sharedFoldersTable="bitTableV2"
  [tableDef]="table"
  [filter]="filter"
  [loading]="loading()"
  queryParam="sharedFolders"
  [trackBy]="trackById"
  [selection]="selection()"
  [virtualRowHeight]="rowHeight"
  height="fill"
>
  <bit-table-toolbar>
    <bit-search class="tw-flex-1" [placeholder]="'search' | i18n"></bit-search>

    <!-- Hidden rather than removed: projection only matches static top-level nodes, so an `@if`
         would keep the button out of the toolbar's `end` slot. -->
    <button
      id="shared-folders_button_add"
      slot="end"
      type="button"
      bitButton
      buttonType="primary"
      startIcon="bwi-plus"
      [class.tw-hidden]="!canAdd()"
      (click)="addSharedFolder()"
    >
      {{ "add" | i18n }}
    </button>

    <bit-filter-toggle
      key="pinned"
      [label]="'pinned' | i18n"
      icon="bwi-pin"
      [disabled]="noPinned() && !sharedFoldersTable.filterValues().pinned"
      [disabledTooltip]="pinnedDisabledTooltip()"
    ></bit-filter-toggle>

    @if (showPermissions()) {
      <bit-filter-menu key="permissions" [placeholderText]="'permissions' | i18n" multiple>
        @for (permission of permissionOptions(); track permission) {
          <bit-filter-option [value]="permission">
            {{ permissionMessageKey(permission) | i18n }}
          </bit-filter-option>
        }
      </bit-filter-menu>
    }

    @if (showControlledAccess()) {
      <bit-filter-toggle
        key="controlledAccess"
        [label]="'controlledAccess' | i18n"
        icon="bwi-lock-encrypted"
      ></bit-filter-toggle>
    }
  </bit-table-toolbar>

  <bit-status-lockup slot="empty">
    <bit-svg slot="graphic" [content]="emptyIcon()"></bit-svg>
    <span slot="title">{{ emptyTitleKey() | i18n }}</span>
    <span slot="description">{{ emptyDescriptionKey() | i18n }}</span>
    <!-- Hidden rather than removed, for the same reason as the Add button above. -->
    <button
      id="shared-folders_button_clear-filters"
      slot="button"
      type="button"
      bitButton
      buttonType="secondary"
      startIcon="bwi-clear"
      class="tw-mb-6"
      [class.tw-hidden]="!hasActiveChipFilters(sharedFoldersTable)"
      (click)="clearChipFilters(sharedFoldersTable)"
    >
      {{ "clearAll" | i18n }}
    </button>
  </bit-status-lockup>

  <!-- Name -->
  <!-- Capped rather than `1fr`: unbounded, the name track would soak up every pixel the other
       columns don't claim and strand them at the far right of a wide table. -->
  <bit-column sortable defaultSort="asc" width="minmax(240px, 480px)">
    <bit-header-cell>{{ "name" | i18n }}</bit-header-cell>

    <bit-cell
      *bitCellDef="table.columns.name; let row"
      class="tw-cursor-pointer"
      (click)="onCellActivate(row, $event)"
    >
      <!-- Decorative: the folder name beside it already names the row. -->
      <bit-icon-tile slot="start" icon="bwi-shared-folder" size="sm" aria-hidden="true" />
      <!-- An anchor, not just the row click, so keyboard users can reach the folder and middle- and
           right-click behave as expected on the name. -->
      <span class="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
        <a
          vaultTableRowName
          [bitTooltip]="row.name"
          [attr.id]="'shared-folders_link_name-' + row.id"
          [routerLink]="sharedFolderCommands(row)"
        >
          {{ row.name }}
        </a>
        @if (isPinned(row)) {
          <bit-icon
            name="bwi-pin"
            class="tw-shrink-0 tw-text-muted"
            [attr.id]="'shared-folders_icon_pinned-' + row.id"
            [ariaLabel]="'pinned' | i18n"
          />
        }
        @if (decoration) {
          <ng-container *ngComponentOutlet="decoration; inputs: { collection: row.collection }" />
        }
      </span>
      @if (row.nestedSharedFolders > 0) {
        <span slot="secondary">
          {{
            row.nestedSharedFolders === 1
              ? ("nestedSharedFolderSingular" | i18n)
              : ("nestedSharedFolderCount" | i18n: row.nestedSharedFolders)
          }}
        </span>
      }
    </bit-cell>
  </bit-column>

  <!-- Permissions -->
  <!-- Wider than the label needs: "View items, hidden passwords" is the longest permission. -->
  <bit-column sortable [sortFn]="sortByPermission" width="minmax(200px, 320px)">
    <bit-header-cell>{{ "permissions" | i18n }}</bit-header-cell>

    <bit-cell
      *bitCellDef="table.columns.permissions; let row"
      class="tw-cursor-pointer"
      (click)="onCellActivate(row, $event)"
    >
      {{ permissionMessageKey(row.permissions) | i18n }}
    </bit-cell>
  </bit-column>

  <!-- Items -->
  <bit-column sortable width="minmax(100px, 160px)">
    <bit-header-cell>{{ "items" | i18n }}</bit-header-cell>

    <bit-cell
      *bitCellDef="table.columns.items; let row"
      class="tw-cursor-pointer"
      (click)="onCellActivate(row, $event)"
    >
      {{ row.items }}
    </bit-cell>
  </bit-column>

  <!-- Options -->
  <!-- The flexible track, so leftover width lands here and the right-aligned overflow trigger
       stays pinned to the table's right edge. -->
  <bit-column width="minmax(80px, 1fr)">
    <bit-header-cell>
      <span class="tw-w-full tw-text-end">{{ "options" | i18n }}</span>
    </bit-header-cell>

    <bit-cell *bitCellLoading>
      <div class="tw-flex tw-justify-end">
        <bit-skeleton-text class="tw-w-6" />
      </div>
    </bit-cell>

    <bit-cell
      *bitCellDef="table.columns.options; let row"
      class="tw-cursor-pointer"
      (click)="onCellActivate(row, $event)"
    >
      <!-- In the `end` slot so the cell's flex-grow middle element pushes the trigger hard right,
           anchoring it to the same position on every row. -->
      <div slot="end">
        <vault-collection-options-menu
          [collection]="row.collection"
          [organization]="organization()"
          [showPin]="true"
          triggerIcon="bwi-ellipsis-v"
          idPrefix="shared-folders"
        />
      </div>
    </bit-cell>
  </bit-column>
</bit-table-v2>

<!-- Rendered into the layout's footer region rather than inline — see the constructor. Outside the
     table, so the bar can't find it by injection; the count and dismiss are wired explicitly.
     Each action is dropped when the member can perform it on none of the listed folders — see
     `showBulkEditAccess`. -->
<ng-template #bulkBar>
  @if (showBulkEditAccess() || showBulkDelete()) {
    <bit-bulk-actions-bar [selectedCount]="selectedRows().length" (clear)="clearSelection()">
      @if (showBulkEditAccess()) {
        <bit-bulk-action
          icon="bwi-users"
          [label]="'editAccess' | i18n"
          [action]="editSelectedAccess"
          [disabled]="bulkEditAccessDisabled()"
        />
      }
      @if (showBulkDelete()) {
        <bit-bulk-action
          icon="bwi-trash"
          [label]="'delete' | i18n"
          [action]="deleteSelected"
          [disabled]="bulkDeleteDisabled()"
        />
      }
    </bit-bulk-actions-bar>
  }
</ng-template>
